import React, { useState } from 'react';
import { Users, FileText, Play, RotateCcw, Download, Palette, Package, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { getTranslation } from '../i18n/translations';
import { toast } from '../hooks/use-toast';
import { useNavigate } from 'react-router-dom';

const Admin = () => {
  const navigate = useNavigate();
  const { lockers, logs, users, whiteLabel, setWhiteLabel, exportLogs, resetDemo, simulateEvent, depositParcel, retrieveParcel, generatePin, language } = useAuth();
  const primaryColor = whiteLabel ? '#2D4A8F' : '#F26724';
  const t = (key) => getTranslation(key, language);
  
  const [filterType, setFilterType] = useState('All');
  const [filterLocker, setFilterLocker] = useState('All');

  const activeLockers = lockers.filter(l => l.state === 'Occupied').length;
  const alertLockers = lockers.filter(l => l.state === 'Alert').length;

  const handleResetDemo = () => {
    if (window.confirm(t('areYouSureReset'))) {
      resetDemo();
      toast({
        title: t('demoReset'),
        description: t('allDataReset'),
      });
    }
  };

  const handlePlayScenario = () => {
    toast({
      title: t('scenarioStarted'),
      description: t('navigateToTimeline'),
    });
    navigate('/timeline');
  };

  const handleExportLogs = () => {
    exportLogs();
    toast({
      title: t('exportSuccessful'),
      description: t('logsExported'),
    });
  };

  // Filter logs
  const filteredLogs = logs.filter(log => {
    if (filterType !== 'All' && log.type !== filterType) return false;
    if (filterLocker !== 'All' && log.locker.toString() !== filterLocker) return false;
    return true;
  });

  const eventTypes = ['All', 'Deposit', 'Retrieve', 'Tamper', 'Battery-low', 'Admin'];
  const lockerNumbers = ['All', ...Array.from({ length: 12 }, (_, i) => (i + 1).toString())];

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h1 className="text-4xl font-bold text-[#1A1A1A]">{t('adminPanel')}</h1>
        <p className="text-gray-600">{t('manageUsers')}</p>
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">{t('totalLockers')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold text-[#1A1A1A]">{lockers.length}</div>
            <p className="text-sm text-gray-500 mt-1">12 {t('unitsTotal')}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">{t('activeLockers')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold" style={{ color: primaryColor }}>{activeLockers}</div>
            <p className="text-sm text-gray-500 mt-1">{t('currentlyOccupied')}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">{t('alerts')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold text-yellow-600">{alertLockers}</div>
            <p className="text-sm text-gray-500 mt-1">{t('requireAttention')}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">{t('totalEvents')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold text-blue-600">{logs.length}</div>
            <p className="text-sm text-gray-500 mt-1">{t('allTimeActivity')}</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="logs" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="logs">{t('logs')}</TabsTrigger>
          <TabsTrigger value="users">{t('usersBadges')}</TabsTrigger>
          <TabsTrigger value="simulation">{t('simulation')}</TabsTrigger>
          <TabsTrigger value="settings">{t('settings')}</TabsTrigger>
        </TabsList>

        <TabsContent value="logs">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>{t('eventLogs')}</CardTitle>
                  <CardDescription>{t('viewFilterActivity')}</CardDescription>
                </div>
                <Button onClick={handleExportLogs} style={{ backgroundColor: primaryColor }}>
                  <Download className="mr-2" size={16} />
                  {t('exportCsv')}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex space-x-4">
                  <div className="flex-1">
                    <label className="text-sm font-medium text-gray-700 block mb-2">{t('eventType')}</label>
                    <select 
                      value={filterType}
                      onChange={(e) => setFilterType(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                    >
                      {eventTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="text-sm font-medium text-gray-700 block mb-2">{t('locker')}</label>
                    <select 
                      value={filterLocker}
                      onChange={(e) => setFilterLocker(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                    >
                      {lockerNumbers.map(num => (
                        <option key={num} value={num}>{num === 'All' ? t('allLockers') : `${t('locker')} #${num}`}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="border rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('time')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('type')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('locker')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('user')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('details')}</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredLogs.slice(0, 50).map((log) => (
                        <tr key={log.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {log.timestamp.toLocaleTimeString()}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-[#1A1A1A]">
                            {log.type}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            #{log.locker}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {log.user || t('na')}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {log.pin && `${t('pin')}: ${log.pin}`}
                            {log.method && `${t('method')}: ${log.method}`}
                            {log.alert && `${t('alert')}: ${log.alert}`}
                            {log.action && `${t('action')}: ${log.action}`}
                            {!log.pin && !log.method && !log.alert && !log.action && t('na')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-sm text-gray-500 text-center">
                  {t('showing')} {Math.min(filteredLogs.length, 50)} {t('of')} {filteredLogs.length} {t('events')}
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="users">
          <Card>
            <CardHeader>
              <CardTitle>{t('usersBadges')}</CardTitle>
              <CardDescription>{t('manageDemoUsers')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {users.map(user => (
                  <div key={user.id} className="p-4 rounded-lg border-2 border-gray-200 hover:border-gray-300 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: `${primaryColor}20` }}>
                          <Users size={24} style={{ color: primaryColor }} />
                        </div>
                        <div>
                          <p className="font-semibold text-lg text-[#1A1A1A]">{user.name}</p>
                          <p className="text-sm text-gray-600">{user.type}</p>
                          {user.rfid && (
                            <p className="text-xs text-gray-500 mt-1">{t('rfid')}: {user.rfid}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {user.rfid ? (
                          <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                            {t('rfidActive')}
                          </span>
                        ) : (
                          <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-medium">
                            {t('pinOnly')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="simulation">
          <Card>
            <CardHeader>
              <CardTitle>{t('simulationControls')}</CardTitle>
              <CardDescription>{t('runPredefined')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <Button 
                  onClick={handlePlayScenario}
                  className="w-full text-lg py-6"
                  style={{ backgroundColor: primaryColor }}
                >
                  <Play className="mr-2" size={20} />
                  {t('playFullDay')}
                </Button>
                
                <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                  <p className="text-sm text-gray-700">
                    {t('fullDayDesc')}
                  </p>
                </div>

                <Button 
                  onClick={handleResetDemo}
                  variant="outline"
                  className="w-full text-lg py-6 border-red-300 text-red-700 hover:bg-red-50"
                >
                  <RotateCcw className="mr-2" size={20} />
                  {t('resetDemo')}
                </Button>
                
                <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200">
                  <p className="text-sm text-yellow-800 font-medium">
                    ⚠️ {t('resetWarning')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle>{t('themeSettings')}</CardTitle>
              <CardDescription>{t('customizeAppearance')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="flex items-center justify-between p-4 rounded-lg border-2 border-gray-200">
                  <div className="flex items-center space-x-4">
                    <Palette size={24} style={{ color: primaryColor }} />
                    <div>
                      <p className="font-semibold text-[#1A1A1A]">{t('whiteLabelMode')}</p>
                      <p className="text-sm text-gray-600">{t('switchNeutral')}</p>
                    </div>
                  </div>
                  <Button 
                    onClick={() => setWhiteLabel(!whiteLabel)}
                    style={{ backgroundColor: whiteLabel ? '#2D4A8F' : '#F26724' }}
                  >
                    {whiteLabel ? t('switchToSwedbank') : t('switchToWhiteLabel')}
                  </Button>
                </div>

                <div className="p-4 rounded-lg bg-gray-50">
                  <p className="text-sm font-medium text-gray-700 mb-4">{t('currentTheme')}</p>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">{t('brandName')}:</span>
                      <span className="text-sm font-semibold text-[#1A1A1A]">
                        {whiteLabel ? 'Smart Locker System' : 'Swedbank Lockers'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">{t('primaryColor')}:</span>
                      <div className="flex items-center space-x-2">
                        <div className="w-6 h-6 rounded" style={{ backgroundColor: primaryColor }} />
                        <span className="text-sm font-semibold text-[#1A1A1A]">{primaryColor}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                  <p className="text-sm text-blue-800">
                    💡 {t('whiteLabelTip')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Admin;
